import groupBy from 'lodash/groupBy';
import memoize from 'lodash/memoize';

import { setActiveExchangeTickets } from '../../actions/exchange-actions';
import { createFilterRequest } from '../../actions/fetch';
import { ActionTypes } from '../../actions/index';
import { fetchSettings } from '../../actions/settings-fetch';
import { removeSeatFromCart, updateCartSeats } from '../../cart/actions/cart-actions';
import { ItemType } from '../../classes/item-type';
import { ISeatMatchRequestModel } from '../../classes/request-model';
import { ISeatMapResult } from '../../classes/result-model';
import { CartSeat } from '../../classes/seat';
import { IExchangeTicket } from '../../classes/ticket-exchange';
import { selectPriceTypeTickets } from '../../filter/actions/filter-actions';
import {
    clearStorageItemsWhere, deleteSeatmapBackground, getSavedSeatmapBackground,
    getSavedSeatRequest, removeOldestSeatMaps, saveSeatmapBackground, saveSeatPaths
} from '../../helpers/local-storage-helpers';
import { getSelectedPriceTypesFromFilter } from '../../helpers/price-type-helpers';
import { getCurrentLanguage } from '../../helpers/window-helpers';
import { IAppState } from '../../reducers/index';
import { loadInCartStyleForSeat, seatmapLoadError } from './seatmap-actions';

/**
 * When the seatmap load errors, notify redux the failure to handle it appropriately.
 */
function requestSeatmap(requestModel: ISeatMatchRequestModel) {
    return {
        type: ActionTypes.REQUEST_SEATMAP,
        value: requestModel
    }
}

function receiveSeatMap(result) {
    return {
        type: ActionTypes.RECEIVE_SEATMAP,
        value: result
    }
}

function loadSeatMap(result, selectedPriceTypes, cartSeats) {
    return {
        type: ActionTypes.LOAD_SEATMAP,
        value: {
            response: result,
            selectedPriceTypes: selectedPriceTypes,
            cartSeats
        }
    }
}

function requestSeatmapBackgroundPaths(seatmapId: string) {
    return {
        type: ActionTypes.REQUEST_SEATMAP_BACKGROUND,
        value: seatmapId
    }
}

function receiveSeatMapBackgroundPaths() {
    return {
        type: ActionTypes.RECEIVE_SEATMAP_BACKGROUND,
        value: true
    }
}

function loadSeatMapBackgroundPaths(result) {
    return {
        type: ActionTypes.LOAD_SEATMAP_BACKGROUND,
        value: {
            response: result,
        }
    }
}

function loadSeatPaths(result, cartSeats) {
    return {
        type: ActionTypes.LOAD_SEATMAP_PATHS,
        value: {
            response: result,
            cartSeats
        }
    }
}

function receiveSeatPaths() {
    return {
        type: ActionTypes.RECEIVE_SEATMAP_PATHS,
        value: {
            response: true,
        }
    }
}

function updateExchangeSeatMatch(requestModel: ISeatMatchRequestModel, seatmapId: string) {
    return fetch(`${window.location.origin}/api/seating/GetExchangeSeatmap`,
        {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({
                itemType: requestModel.itemType,
                itemId: requestModel.itemId,
                minPrice: requestModel.minPrice,
                maxPrice: requestModel.maxPrice,
                //quantity: requestModel.seatQuantity,
                allowSeparatedSeats: requestModel.allowSeparatedSeats,
                priceTypeQuantities: requestModel.requestedPriceTypes,
                venueLevels: requestModel.selectedVenueLevels,
                selectedAllLevels: requestModel.selectedAllLevels,
                allowAisleAccessSelected: requestModel.allowAisleAccessSelected,
                additionalSeatingOptions: requestModel.additionalSeatingOptions,
                seatMapId: seatmapId,
                language: getCurrentLanguage()
            }),
            credentials: 'same-origin'
        })
        .then(response => {
            if (response.ok) {
                return response.json();
            } else {
                return "ERROR";
            }
        })
        .catch(error => {
            console.error(error);
        });
}

function updateSeatMatch(requestModel: ISeatMatchRequestModel, seatmapId: string, isSyosOnly: boolean, seatsToIgnore: Array<number>) {
    return fetch(`${window.location.origin}/api/seating/GetSeatmap`,
        {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({
                itemType: requestModel.itemType,
                itemId: requestModel.itemId,
                minPrice: requestModel.minPrice,
                maxPrice: requestModel.maxPrice,
                //quantity: requestModel.seatQuantity,
                allowSeparatedSeats: requestModel.allowSeparatedSeats,
                priceTypeQuantities: requestModel.requestedPriceTypes,
                venueLevels: requestModel.selectedVenueLevels,
                selectedAllLevels: requestModel.selectedAllLevels,
                allowAisleAccessSelected: requestModel.allowAisleAccessSelected,
                additionalSeatingOptions: requestModel.additionalSeatingOptions,
                seatMapId: seatmapId,
                language: getCurrentLanguage(),
                seatsToIgnore: seatsToIgnore,
                isSyosOnly: isSyosOnly
            }),
            credentials: 'same-origin'
        })
        .then(response => {
            if (response.ok) {
                return response.json();
            } else {
                return "ERROR";
            }
        })
        .catch(error => {
            console.error(error);
        });
}

function updateSeatMapBackground(seatmapId: string) {
    return fetch(`/api/seating/GetSeatmapBackgroundPaths?seatmapId=${seatmapId}`, {
        credentials: 'same-origin'
    }).then(response => {
        if (!response.ok) {
            throw Error(response.statusText);
        } else {
            return response.json();
        }
    });
}

function updateSeatPaths(itemId: number, itemType: ItemType, language: string, seatmapId: string) {
    return fetch(`/api/seating/GetSeatmapSeats?itemId=${itemId}&itemType=${itemType}&language=${language}&seatmapId=${seatmapId}`, {
        credentials: 'same-origin'
    }).then(response => {
        if (!response.ok) {
            throw Error(response.statusText);
        } else {
            return response.json();
        }
    });
}

export function fetchSeatMap(seatmapId: string, cartSeats: Array<CartSeat>, forPromoCodeApply: boolean, promoPriceTypeMapping: Record<number, number>) {
    return (dispatch, state) => {
        const currentState: IAppState = state();

        // need to refetch settings to get new price types and promo code
        if (forPromoCodeApply) {
            dispatch(fetchSettings(currentState.appSettingsModel.itemId, currentState.appSettingsModel.itemType, currentState.appSettingsModel.language));
        }

        let seatMatchRequest: ISeatMatchRequestModel = null;

        const exchangeTicketsGrouped = groupBy(currentState.appSettingsModel.exchangeTickets, (ticket: IExchangeTicket) => ticket.oldPrice);
        const activeExchangeTickets = exchangeTicketsGrouped[Object.keys(exchangeTicketsGrouped)[0]];
        //this state shouldn't happen anymore with new bootstrapped idea

        const appSettings = currentState.appSettingsModel;

        if (currentState.appSettingsModel.itemId === 0) {
            window.location.href = window.location.href.split('#')[0];
        } else if (getSavedSeatRequest(appSettings.itemId, appSettings.itemType)) {
            try {
                seatMatchRequest = JSON.parse(getSavedSeatRequest(appSettings.itemId, appSettings.itemType)) as ISeatMatchRequestModel;

                seatMatchRequest.requestedPriceTypes.forEach(pt => {
                    if (pt.quantity > 0) {
                        dispatch(selectPriceTypeTickets(pt.priceTypeId, pt.quantity, false, activeExchangeTickets, false));
                    }
                });
            } catch (ex) {
                console.log("bad stored seat request")
            }
        } else {
            seatMatchRequest = createFilterRequest(currentState);
        }

        if (seatMatchRequest !== null) {

            // Disable default selection in SYOS only
            if (appSettings.isSelectSeatVenue) {
                seatMatchRequest.requestedPriceTypes = []
            }

            dispatch(requestSeatmap(seatMatchRequest));

            if (currentState.appSettingsModel.exchangeTickets.length > 0) {
                updateExchangeSeatMatch(seatMatchRequest, seatmapId)
                    .then((response: any) => {
                        if (response === "ERROR" || response === undefined) {
                            dispatch(receiveSeatMap('Error fetching seats'));
                            dispatch(seatmapLoadError());
                        } else {
                            dispatch(loadSeatMap(response, seatMatchRequest.requestedPriceTypes.filter(pt => { return pt.quantity > 0 }), cartSeats));
                            dispatch(setActiveExchangeTickets(activeExchangeTickets, []));
                            loadSeatMapBackground(dispatch, response);
                            let currentState: IAppState = state();

                            currentState.cartModel.seats.forEach(seat =>
                                dispatch(loadInCartStyleForSeat(seat))
                            );

                            dispatch(receiveSeatMap(response));
                        }
                    }).catch(error => {
                        console.error(error);
                        dispatch(seatmapLoadError());
                    });
            } else {
                updateSeatMatch(seatMatchRequest, seatmapId, appSettings.isSelectSeatVenue, cartSeats.map(cartSeat => cartSeat.tessituraSeat.seatNumber))
                    .then((response: ISeatMapResult) => {
                        if (response.toString() === "ERROR" || response === undefined) {
                            dispatch(receiveSeatMap('Error fetching seats'));
                            dispatch(seatmapLoadError());
                        } else {
                            let updatedCartSeats: Array<CartSeat> = [];

                            if (forPromoCodeApply) {

                                cartSeats.forEach(cartSeat => {
                                    var matchedZones = response.allSeatPricing.filter(pr => pr.zoneId === (+cartSeat.tessituraSeat.zoneId));
                                    if (matchedZones.length > 0) {
                                        const matchedZone = matchedZones[0];
                                        // has pricing to choose from
                                        const newState: IAppState = state();
                                        var selectedPriceTypes = getSelectedPriceTypesFromFilter(newState.priceTypesModel,
                                            newState.appSettingsModel.selectAllPriceTypesOnSeatmap,
                                            (!newState.appSettingsModel.selectAllPriceTypesOnSeatmap && newState.appSettingsModel.selectOnlyDefaultPriceTypesOnSeatmap));

                                        if (matchedZone.prices.length > 0) {
                                            if (matchedZone.prices.length === 1) {
                                                updatedCartSeats.push({ ...cartSeat, selectedPricing: { ...matchedZone.prices[0], priceCarted: matchedZone.prices[0].price } });
                                            } else if (matchedZone.prices.filter(pr => selectedPriceTypes.map(pt => pt.priceTypeId).indexOf(pr.priceTypeId) >= 0).length === 1) {

                                                var matchedPr = matchedZone.prices.filter(pr => selectedPriceTypes.map(pt => pt.priceTypeId).indexOf(pr.priceTypeId) >= 0)[0];
                                                updatedCartSeats.push({ ...cartSeat, selectedPricing: { ...matchedPr, priceCarted: matchedPr.price } });
                                            } else {
                                                if (Object.keys(promoPriceTypeMapping).length > 0) {

                                                    var newPriceType = promoPriceTypeMapping[cartSeat.selectedPricing.priceTypeId];

                                                    if (newPriceType) {
                                                        var matchedNewPt = matchedZone.prices.filter(pr => pr.priceTypeId === newPriceType);
                                                        if (matchedNewPt.length > 0) {
                                                            updatedCartSeats.push({ ...cartSeat, selectedPricing: { ...matchedNewPt[0], priceCarted: matchedNewPt[0].price } });
                                                        }
                                                    }
                                                } else {
                                                    cartSeats = [];
                                                }
                                                // need a mapping from server side so dev can give it logic
                                            }
                                        }
                                    }
                                });

                                const newState: IAppState = state();
                                // check for price type max. If the number of seats is great than new max, then clear out seats
                                const overMaxPriceTypes = newState.priceTypesModel.priceTypes.filter(pt => {
                                    return updatedCartSeats.filter(cartSeat => cartSeat.selectedPricing.priceTypeId === pt.priceTypeId).length > pt.clientMaxQuantity;
                                }).length > 0;

                                if (overMaxPriceTypes) {
                                    updatedCartSeats.forEach(item =>
                                        dispatch(removeSeatFromCart(item, newState.priceTypesModel.priceTypes, appSettings.brandId, false))
                                    );

                                    updatedCartSeats = [];
                                    cartSeats = [];
                                    //dispatch(updateCartSeats([]));
                                } else {
                                    // should we only add all of the seats or none.. or only add the working ones
                                    dispatch(updateCartSeats(updatedCartSeats));
                                }
                            }

                            dispatch(loadSeatMap(response, seatMatchRequest.requestedPriceTypes, updatedCartSeats.length > 0 ? updatedCartSeats : cartSeats));
                            loadSeatMapBackground(dispatch, response);
                            let currentState: IAppState = state();

                            //var suggestSeats = _.orderBy(response.levelSeats.filter(seat => {
                            //    return seat.suggestedSeat && seat.tessituraSeat.isAvailable;
                            //}), 'logicalSeatNumber');
                            //if (suggestSeats.length > 0) {
                            //    // Not needed for single-click.
                            //    //dispatch(selectSeatAndSetCoordinates(suggestSeats[0] as Seat));
                            //}
                            currentState.cartModel.seats.forEach(seat =>
                                dispatch(loadInCartStyleForSeat(seat))
                            );

                            dispatch(receiveSeatMap(response));
                        }
                    }).catch(error => {
                        console.error(error);
                        dispatch(seatmapLoadError());
                    });
            }
        }
    }
}

function loadSeatMapBackground(dispatch, response) {
    var savedPaths = getSavedSeatmapBackground(response.seatmapId);

    if (savedPaths) {
        dispatch(loadSeatMapBackgroundPaths(JSON.parse(savedPaths)));
        dispatch(receiveSeatMapBackgroundPaths());
    } else {
        //need to fetch for the first time
        dispatch(requestSeatmapBackgroundPaths(response.seatmapId));
    }
    dispatch(fetchSeatMapBackground(response.seatmapId));
}

export function fetchSeatMapBackground(seatmapId: string) {
    return (dispatch, state) => {
        let currentState: IAppState = state();

        if (seatmapId !== null) {
            updateSeatMapBackground(seatmapId)
                .then((response: any) => {
                    if (response === "ERROR" || response === undefined) {
                        dispatch(receiveSeatMap('Error fetching background paths'));
                        dispatch(seatmapLoadError());
                    } else {
                        var savedPaths = getSavedSeatmapBackground(seatmapId);
                        deleteSeatmapBackground(seatmapId);

                        if (!savedPaths) {
                            dispatch(loadSeatMapBackgroundPaths(response.backgroundPaths));
                            dispatch(receiveSeatMapBackgroundPaths());
                        }

                        var packedstring = JSON.stringify(response.backgroundPaths);
                        try {
                            saveSeatmapBackground(seatmapId, packedstring, response.timestamp);
                        } catch (err) {
                            console.log(`LOG: LocalStorage is full (saving for item ${currentState.appSettingsModel.itemId}). Deleting older seatmaps to free up space.`);
                            removeOldestSeatMaps();
                            try {
                                saveSeatmapBackground(seatmapId, packedstring, response.timestamp);
                            }
                            catch {
                                console.log(`LOG: LocalStorage is still too full (saving for item ${currentState.appSettingsModel.itemId}) after freeing up space.`);
                            }
                        }
                    }
                }).catch(error => {
                    console.error(error);
                    dispatch(seatmapLoadError());
                });
        }
    }
}

export function fetchSeatPaths(itemId: number, itemType: ItemType, language: string, seatmapId: string, cartSeat: Array<CartSeat>) {
    return (dispatch, state) => {
        let currentState: IAppState = state();

        if (seatmapId !== null) {
            updateSeatPaths(itemId, itemType, language, seatmapId)
                .then((response: any) => {
                    if (response === "ERROR" || response === undefined) {
                        dispatch(receiveSeatMap('Error fetching seat paths'));
                        dispatch(seatmapLoadError());
                    } else {
                        clearStorageItemsWhere(key => key.indexOf(`seatpaths:${seatmapId},${response.brandId}`) >= 0);

                        //if (savedPaths == null) {
                        dispatch(loadSeatPaths(response.seatPaths, currentState.cartModel.seats));
                        dispatch(receiveSeatPaths());
                        //}

                        try {
                            saveSeatPaths(seatmapId, response.brandId, JSON.stringify(response.seatPaths), response.timestamp);
                        } catch (err) {
                            console.log("LOG: LocalStorage is full (saving for item " + currentState.appSettingsModel.itemId);
                        }
                    }
                }).catch(error => {
                    console.error(error);
                    dispatch(seatmapLoadError());
                });
        }
    }
}

export function getSeatViewUrl(seatViewImageId: string) {
    if (!seatViewImageId) return null;
    return `${window.location.origin}/api/seating/GetSeatViewImageFile?seatViewImageId=${seatViewImageId}`;
}

export function getSeatViewThumbUrl(seatViewImageId: string) {
    if (!seatViewImageId) return null;
    return `${window.location.origin}/api/seating/GetSeatViewImageFile?seatViewImageId=${seatViewImageId}&thumbnail=true`;
}